/**
 * A person's own preferences, kept on the server (v7 CORE-1 §3, Part D §10.6): the look, density, and the sidebar
 * items pinned to the top. Read once per session; each change is written back.
 *
 * Classic for everybody (the owner's Q0.8): a person who has not chosen a look on the server gets Classic, whatever an
 * older browser remembered; their own choice, once made, follows them to any computer.
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { rpc } from './supabase';
import { useSession } from './session';
import { useAppearance, type Appearance } from '../shell/appearance';

export interface Preferences { appearance?: Partial<Appearance>; pinned?: string[]; toursSeen?: string[] }

// The owner, 2026-10-18 (CRM brief): the Classic style in the Desigo palette, unless the person chose their own look.
export const CLASSIC: Partial<Appearance> = { theme: 'desigo', style: 'classic', canvas: 'motifs' };

export function usePreferences() {
  const { me } = useSession();
  return useQuery({ queryKey: ['my-preferences'], enabled: !!me, staleTime: Infinity, retry: false, queryFn: () => rpc<Preferences>('my_preferences') });
}

export function useSavePreferences() {
  const qc = useQueryClient();
  return async (next: Preferences) => {
    const saved = await rpc<Preferences>('set_my_preferences', { p_prefs: next });
    qc.setQueryData(['my-preferences'], saved);
    return saved;
  };
}

/** Mounted once in the shell: applies the person's saved look, or Classic when they have not chosen one. */
export function useApplySavedAppearance() {
  const prefs = usePreferences();
  const [, setAppearance] = useAppearance();
  useEffect(() => {
    if (!prefs.data) return;
    setAppearance({ ...CLASSIC, ...(prefs.data.appearance ?? {}) });
    // Applied once per answer from the server; later changes come from the person's own choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.data]);
}
